import { colorWithAlpha, toTradeNumber } from "./chartOverlayUtils";
import {
  calculateTradeMetrics,
  formatSignedTradeCurrency,
  formatTradeQuantity,
} from "./chartTradeMetrics";

export const DRAWING_HIT_TOLERANCE_PX = 8;
export const DRAWING_HANDLE_RADIUS_PX = 5;
export const DRAWING_MIN_DRAG_PX = 12;

export const applyHorizontalLineOverlayToPriceLine = ({
  overlay,
  priceLineById,
}) => {
  if (
    String(overlay?.kind || "").toLowerCase() !== "horizontal_line" ||
    !overlay?.id ||
    !Number.isFinite(Number(overlay.price))
  ) {
    return false;
  }

  const priceLine = priceLineById?.get?.(String(overlay.id));
  if (typeof priceLine?.applyOptions !== "function") return false;

  priceLine.applyOptions({ price: Number(overlay.price) });
  return true;
};

const finiteTime = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const canonicalAnchor = (value) => {
  if (!value || typeof value !== "object") return null;
  const time = finiteTime(value.time ?? value.timestamp);
  const price = toTradeNumber(value.price ?? value.value);
  if (time == null || price == null) return null;
  const futureOriginTime = finiteTime(
    value.future_origin_time ?? value.futureOriginTime,
  );
  const futureOffset = Number(value.future_offset ?? value.futureOffset);
  const projection =
    futureOriginTime != null &&
    Number.isInteger(futureOffset) &&
    futureOffset > 0
      ? {
          future_origin_time: futureOriginTime,
          future_offset: futureOffset,
        }
      : {};
  return { time, price, ...projection };
};

const overlayAnchor = (overlay, endpoint) =>
  canonicalAnchor(
    overlay?.[`${endpoint}_anchor`] ||
      overlay?.[`${endpoint}Anchor`] ||
      {
        time: overlay?.[`${endpoint}_time`] ?? overlay?.[`${endpoint}Time`],
        price: overlay?.[`${endpoint}_price`] ?? overlay?.[`${endpoint}Price`],
      },
  );

const nearestTimeIndex = (priceTimes, rawTime) => {
  if (!Array.isArray(priceTimes) || priceTimes.length === 0) return null;
  const target = finiteTime(rawTime);
  if (target == null) return null;

  let low = 0;
  let high = priceTimes.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const value = Number(priceTimes[middle]);
    if (value === target) return middle;
    if (value < target) low = middle + 1;
    else high = middle - 1;
  }

  const left = Math.max(0, Math.min(high, priceTimes.length - 1));
  const right = Math.max(0, Math.min(low, priceTimes.length - 1));
  return Math.abs(Number(priceTimes[left]) - target) <=
    Math.abs(Number(priceTimes[right]) - target)
    ? left
    : right;
};

const estimatedTimeStep = (priceTimes) => {
  if (!Array.isArray(priceTimes) || priceTimes.length < 2) return 1;
  const last = Number(priceTimes[priceTimes.length - 1]);
  const previous = Number(priceTimes[priceTimes.length - 2]);
  const step = last - previous;
  return Number.isFinite(step) && step > 0 ? step : 1;
};

const logicalIndexForAnchor = (priceTimes, anchor) => {
  const normalized = canonicalAnchor(anchor);
  if (!normalized || !Array.isArray(priceTimes) || priceTimes.length === 0) {
    return null;
  }

  const lastIndex = priceTimes.length - 1;
  const lastTime = Number(priceTimes[lastIndex]);
  if (normalized.time > lastTime) {
    return lastIndex + Math.max(
      1,
      Math.round((normalized.time - lastTime) / estimatedTimeStep(priceTimes)),
    );
  }

  return nearestTimeIndex(priceTimes, normalized.time);
};

const anchorForLogicalIndex = ({ priceTimes, logical, price }) => {
  if (!Array.isArray(priceTimes) || priceTimes.length === 0) return null;
  const index = Math.max(0, Math.round(Number(logical)));
  const lastIndex = priceTimes.length - 1;
  if (index <= lastIndex) {
    const time = Number(priceTimes[index]);
    return Number.isFinite(time) ? { time, price } : null;
  }

  const futureOffset = index - lastIndex;
  const futureOriginTime = Number(priceTimes[lastIndex]);
  if (!Number.isFinite(futureOriginTime)) return null;
  return {
    time: futureOriginTime + futureOffset * estimatedTimeStep(priceTimes),
    price,
    future_origin_time: futureOriginTime,
    future_offset: futureOffset,
  };
};

const pointForAnchor = ({ chart, series, priceTimes, anchor }) => {
  const normalized = canonicalAnchor(anchor);
  if (!normalized) return null;
  const index = logicalIndexForAnchor(priceTimes, normalized);
  if (index == null) return null;

  const x = chart?.timeScale?.()?.logicalToCoordinate?.(index);
  const y = series?.priceToCoordinate?.(normalized.price);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return {
    x,
    y,
    index,
    time: normalized.time,
    price: normalized.price,
  };
};

const timestampSeconds = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric > 1_000_000_000_000 ? numeric / 1000 : numeric;
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? parsed / 1000 : null;
};

const executionPoint = ({ chart, series, priceTimes, time, price }) => {
  const timestamp = timestampSeconds(time);
  const executionPrice = toTradeNumber(price);
  if (
    timestamp == null ||
    executionPrice == null ||
    !Array.isArray(priceTimes) ||
    priceTimes.length === 0
  ) {
    return null;
  }
  const first = Number(priceTimes[0]);
  const last = Number(priceTimes[priceTimes.length - 1]);
  const tolerance = estimatedTimeStep(priceTimes);
  if (timestamp < first - tolerance || timestamp > last + tolerance) return null;

  const index = nearestTimeIndex(priceTimes, timestamp);
  const x = chart?.timeScale?.()?.logicalToCoordinate?.(index);
  const y = series?.priceToCoordinate?.(executionPrice);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y, index, time: timestamp, price: executionPrice };
};

const overlaysWithDraft = (overlays, draftOverlay) => {
  const rows = Array.isArray(overlays) ? overlays : [];
  if (!draftOverlay?.id) return rows;
  let replaced = false;
  const merged = rows.map((overlay) => {
    if (String(overlay?.id) !== String(draftOverlay.id)) return overlay;
    replaced = true;
    return { ...overlay, ...draftOverlay };
  });
  if (!replaced) merged.push(draftOverlay);
  return merged;
};

export const drawingAnchorForChartPoint = ({
  chart,
  series,
  priceTimes,
  x,
  y,
}) => {
  if (!Array.isArray(priceTimes) || priceTimes.length === 0) return null;
  const timeScale = chart?.timeScale?.();
  let logical = timeScale?.coordinateToLogical?.(x);
  if (!Number.isFinite(logical)) {
    const visibleRange = timeScale?.getVisibleLogicalRange?.();
    const plotWidth = Number(timeScale?.width?.());
    if (
      visibleRange &&
      Number.isFinite(visibleRange.from) &&
      Number.isFinite(visibleRange.to) &&
      Number.isFinite(plotWidth) &&
      plotWidth > 0
    ) {
      logical =
        visibleRange.from +
        (Math.max(0, Math.min(Number(x), plotWidth)) / plotWidth) *
          (visibleRange.to - visibleRange.from);
    }
  }
  const price = Number(series?.coordinateToPrice?.(y));
  if (!Number.isFinite(logical) || !Number.isFinite(price)) return null;

  const index = Math.max(0, Math.round(logical));
  const anchor = anchorForLogicalIndex({ priceTimes, logical: index, price });
  return anchor ? { index, logical: index, ...anchor } : null;
};

export const buildEditableOverlayGeometry = ({
  chart,
  series,
  overlays,
  priceTimes,
  width,
  height,
  draftOverlay = null,
}) => {
  if (!chart || !series || !Number.isFinite(width) || !Number.isFinite(height)) {
    return [];
  }

  const timeScale = chart.timeScale?.();
  const plotWidth = Math.max(
    0,
    Math.min(Number(timeScale?.width?.()) || width, width),
  );

  return overlaysWithDraft(overlays, draftOverlay).reduce((geometry, overlay) => {
        if (!overlay || typeof overlay !== "object" || !overlay.id) return geometry;
        const kind = String(overlay.kind || "").toLowerCase();
        const color = overlay.color || "#94a3b8";

        if (kind === "horizontal_line") {
          const price = toTradeNumber(overlay.price ?? overlay.value);
          const y = price == null ? null : series.priceToCoordinate?.(price);
          if (!Number.isFinite(y)) return geometry;
          geometry.push({
            id: String(overlay.id),
            kind,
            overlay,
            color,
            x1: 0,
            x2: plotWidth,
            y1: y,
            y2: y,
          });
          return geometry;
        }

        if (kind === "price_zone" || kind === "price_zone_box") {
          const topPrice = toTradeNumber(overlay.top ?? overlay.upper ?? overlay.high);
          const bottomPrice = toTradeNumber(
            overlay.bottom ?? overlay.lower ?? overlay.low,
          );
          const yTop =
            topPrice == null ? null : series.priceToCoordinate?.(topPrice);
          const yBottom =
            bottomPrice == null ? null : series.priceToCoordinate?.(bottomPrice);
          if (!Number.isFinite(yTop) || !Number.isFinite(yBottom)) return geometry;
          geometry.push({
            id: String(overlay.id),
            kind: "price_zone",
            overlay,
            color,
            x1: 0,
            x2: plotWidth,
            y1: Math.min(yTop, yBottom),
            y2: Math.max(yTop, yBottom),
          });
          return geometry;
        }

        if (kind === "trade_setup") {
          const entry = toTradeNumber(overlay.entry_price ?? overlay.entryPrice);
          const stop = toTradeNumber(overlay.stop_loss ?? overlay.stopLoss);
          const target = toTradeNumber(
            overlay.price_target ?? overlay.priceTarget ?? overlay.profit_target,
          );
          const entryY = entry == null ? null : series.priceToCoordinate?.(entry);
          const stopY = stop == null ? null : series.priceToCoordinate?.(stop);
          const targetY = target == null ? null : series.priceToCoordinate?.(target);
          const lifecycleStatus = String(overlay.lifecycle_status || "").toLowerCase();
          if (lifecycleStatus === "closed") {
            const entryPoint = executionPoint({
              chart,
              series,
              priceTimes,
              time: overlay.activated_at,
              price: overlay.fill_price ?? entry,
            });
            const exitPoint = executionPoint({
              chart,
              series,
              priceTimes,
              time: overlay.closed_at,
              price: overlay.exit_price,
            });
            if (!entryPoint && !exitPoint) return geometry;
            geometry.push({
              id: String(overlay.id),
              kind: "trade_execution",
              overlay,
              color,
              entryPoint,
              exitPoint,
              plotWidth,
              plotHeight: height,
            });
            return geometry;
          }
          if (![entryY, stopY, targetY].every(Number.isFinite)) return geometry;
          geometry.push({
            id: String(overlay.id),
            kind,
            overlay,
            color,
            x1: Math.max(0, plotWidth * 0.34),
            x2: plotWidth,
            entryY,
            stopY,
            targetY,
          });
          return geometry;
        }

        if (kind === "trend_line") {
          const start = pointForAnchor({
            chart,
            series,
            priceTimes,
            anchor: overlayAnchor(overlay, "start"),
          });
          const end = pointForAnchor({
            chart,
            series,
            priceTimes,
            anchor: overlayAnchor(overlay, "end"),
          });
          if (!start || !end) return geometry;
          geometry.push({
            id: String(overlay.id),
            kind,
            overlay,
            color,
            x1: start.x,
            y1: start.y,
            x2: end.x,
            y2: end.y,
            start,
            end,
          });
        }
        return geometry;
    }, []);
};

const pointDistance = (left, right) =>
  Math.hypot(left.x - right.x, left.y - right.y);

const segmentDistance = (point, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const denominator = dx * dx + dy * dy;
  if (denominator <= 0) return pointDistance(point, start);
  const projection = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / denominator,
    ),
  );
  return pointDistance(point, {
    x: start.x + projection * dx,
    y: start.y + projection * dy,
  });
};

export const hitTestEditableOverlay = ({
  geometry,
  x,
  y,
  tolerance = DRAWING_HIT_TOLERANCE_PX,
  selectedId = null,
}) => {
  const point = { x, y };
  const rows = [...(Array.isArray(geometry) ? geometry : [])].sort((left, right) => {
    if (String(left.id) === String(selectedId)) return 1;
    if (String(right.id) === String(selectedId)) return -1;
    return 0;
  });

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const item = rows[index];
    if (item.kind === "trade_execution") {
      const marker = [item.entryPoint, item.exitPoint].find(
        (candidate) =>
          candidate && pointDistance(point, candidate) <= tolerance + 10,
      );
      if (marker) return { item, handle: "execution" };
      continue;
    }
    if (item.kind === "trend_line") {
      if (
        pointDistance(point, { x: item.x1, y: item.y1 }) <=
        tolerance + DRAWING_HANDLE_RADIUS_PX
      ) {
        return { item, handle: "start" };
      }
      if (
        pointDistance(point, { x: item.x2, y: item.y2 }) <=
        tolerance + DRAWING_HANDLE_RADIUS_PX
      ) {
        return { item, handle: "end" };
      }
      if (
        segmentDistance(
          point,
          { x: item.x1, y: item.y1 },
          { x: item.x2, y: item.y2 },
        ) <= tolerance
      ) {
        return { item, handle: "body" };
      }
      continue;
    }

    if (item.kind === "horizontal_line") {
      if (
        x >= item.x1 - tolerance &&
        x <= item.x2 + tolerance &&
        Math.abs(y - item.y1) <= tolerance
      ) {
        return { item, handle: "body" };
      }
      continue;
    }

    if (item.kind === "price_zone") {
      if (
        x >= item.x1 - tolerance &&
        x <= item.x2 + tolerance &&
        Math.abs(y - item.y1) <= tolerance
      ) {
        return { item, handle: "top" };
      }
      if (
        x >= item.x1 - tolerance &&
        x <= item.x2 + tolerance &&
        Math.abs(y - item.y2) <= tolerance
      ) {
        return { item, handle: "bottom" };
      }
      if (
        x >= item.x1 &&
        x <= item.x2 &&
        y >= item.y1 &&
        y <= item.y2
      ) {
        return { item, handle: "body" };
      }
      continue;
    }

    if (item.kind === "trade_setup") {
      if (x < item.x1 - tolerance || x > item.x2 + tolerance) continue;
      if (Math.abs(y - item.targetY) <= tolerance) return { item, handle: "target" };
      if (Math.abs(y - item.stopY) <= tolerance) return { item, handle: "stop" };
      if (Math.abs(y - item.entryY) <= tolerance) return { item, handle: "entry" };
      const minimumY = Math.min(item.targetY, item.stopY);
      const maximumY = Math.max(item.targetY, item.stopY);
      if (y >= minimumY && y <= maximumY) return { item, handle: "body" };
    }
  }

  return null;
};

const clampedIndexDelta = (indexes, requestedDelta) => {
  const minimumIndex = Math.min(...indexes);
  return Math.max(-minimumIndex, requestedDelta);
};

export const moveEditableOverlay = ({
  overlay,
  handle,
  dragStart,
  current,
  priceTimes,
}) => {
  if (!overlay || !dragStart || !current) return overlay;
  const kind = String(overlay.kind || "").toLowerCase();

  if (kind === "horizontal_line") {
    return { ...overlay, price: current.price };
  }

  if (kind === "price_zone" || kind === "price_zone_box") {
    const top = toTradeNumber(overlay.top ?? overlay.upper ?? overlay.high);
    const bottom = toTradeNumber(overlay.bottom ?? overlay.lower ?? overlay.low);
    if (top == null || bottom == null) return overlay;
    if (handle === "top") {
      return {
        ...overlay,
        top: Math.max(current.price, bottom),
        bottom: Math.min(current.price, bottom),
      };
    }
    if (handle === "bottom") {
      return {
        ...overlay,
        top: Math.max(top, current.price),
        bottom: Math.min(top, current.price),
      };
    }
    const priceDelta = current.price - dragStart.price;
    return {
      ...overlay,
      top: top + priceDelta,
      bottom: bottom + priceDelta,
    };
  }

  if (kind === "trade_setup") {
    const entry = toTradeNumber(overlay.entry_price ?? overlay.entryPrice);
    const stop = toTradeNumber(overlay.stop_loss ?? overlay.stopLoss);
    const target = toTradeNumber(overlay.price_target ?? overlay.priceTarget);
    if (entry == null || stop == null || target == null) return overlay;
    const lifecycleStatus = String(overlay.lifecycle_status || "").toLowerCase();
    if (lifecycleStatus === "closed") return overlay;
    if (
      lifecycleStatus === "active" &&
      !["target", "stop"].includes(handle)
    ) {
      return overlay;
    }
    const direction = String(overlay.direction || "long").toLowerCase();
    const minimumDistance = Math.max(Math.abs(entry) * 0.0001, 0.0001);
    if (handle === "target") {
      const price = direction === "short"
        ? Math.min(current.price, entry - minimumDistance)
        : Math.max(current.price, entry + minimumDistance);
      return { ...overlay, price_target: price };
    }
    if (handle === "stop") {
      const price = direction === "short"
        ? Math.max(current.price, entry + minimumDistance)
        : Math.min(current.price, entry - minimumDistance);
      return { ...overlay, stop_loss: price };
    }
    const priceDelta = current.price - dragStart.price;
    return {
      ...overlay,
      entry_price: entry + priceDelta,
      stop_loss: stop + priceDelta,
      price_target: target + priceDelta,
    };
  }

  if (kind !== "trend_line") return overlay;
  const start = overlayAnchor(overlay, "start");
  const end = overlayAnchor(overlay, "end");
  if (!start || !end) return overlay;

  if (handle === "start") {
    return {
      ...overlay,
      start_anchor: canonicalAnchor(current),
    };
  }
  if (handle === "end") {
    return {
      ...overlay,
      end_anchor: canonicalAnchor(current),
    };
  }

  const startIndex = logicalIndexForAnchor(priceTimes, start);
  const endIndex = logicalIndexForAnchor(priceTimes, end);
  if (startIndex == null || endIndex == null) return overlay;
  const requestedDelta =
    Number(current.logical ?? current.index) -
    Number(dragStart.logical ?? dragStart.index);
  const indexDelta = clampedIndexDelta([startIndex, endIndex], requestedDelta);
  const priceDelta = current.price - dragStart.price;
  return {
    ...overlay,
    start_anchor: anchorForLogicalIndex({
      priceTimes,
      logical: startIndex + indexDelta,
      price: start.price + priceDelta,
    }),
    end_anchor: anchorForLogicalIndex({
      priceTimes,
      logical: endIndex + indexDelta,
      price: end.price + priceDelta,
    }),
  };
};

export const drawingGeometryLength = (item) => {
  if (!item) return 0;
  return Math.hypot(item.x2 - item.x1, item.y2 - item.y1);
};

const drawHandle = (context, x, y, color) => {
  context.beginPath();
  context.arc(x, y, DRAWING_HANDLE_RADIUS_PX, 0, Math.PI * 2);
  context.fillStyle = "#ffffff";
  context.fill();
  context.lineWidth = 2;
  context.strokeStyle = color;
  context.stroke();
};

const drawExecutionArrow = ({
  context,
  point,
  pointing,
  color,
  label,
  selected,
  plotWidth,
  plotHeight,
}) => {
  if (!point) return;
  const direction = pointing === "down" ? -1 : 1;
  const tipY = point.y;
  const baseY = tipY + direction * 10;
  const stemEndY = tipY + direction * 18;

  context.save();
  if (selected) {
    context.beginPath();
    context.arc(point.x, point.y, 14, 0, Math.PI * 2);
    context.fillStyle = colorWithAlpha(color, 0.18);
    context.fill();
  }
  context.beginPath();
  context.moveTo(point.x, tipY);
  context.lineTo(point.x - 6, baseY);
  context.lineTo(point.x + 6, baseY);
  context.closePath();
  context.fillStyle = color;
  context.fill();
  context.strokeStyle = color;
  context.lineWidth = 3;
  context.beginPath();
  context.moveTo(point.x, baseY - direction);
  context.lineTo(point.x, stemEndY);
  context.stroke();

  context.font = "750 9px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  const labelWidth = context.measureText(label).width + 12;
  const labelHeight = 18;
  const preferredX = point.x + 10;
  const labelX = Math.max(2, Math.min(preferredX, plotWidth - labelWidth - 2));
  const preferredY = pointing === "down" ? stemEndY - labelHeight : stemEndY;
  const labelY = Math.max(2, Math.min(preferredY, plotHeight - labelHeight - 2));
  context.fillStyle = colorWithAlpha("#0f172a", 0.92);
  context.fillRect(labelX, labelY, labelWidth, labelHeight);
  context.fillStyle = color;
  context.fillRect(labelX, labelY, 2, labelHeight);
  context.fillStyle = "#f8fafc";
  context.textAlign = "left";
  context.textBaseline = "middle";
  context.fillText(label, labelX + 7, labelY + labelHeight / 2);
  context.restore();
};

const tradeLifecyclePresentation = (overlay, direction, entry) => {
  const status = String(overlay.lifecycle_status || "").toLowerCase();
  const side = direction === "short" ? "SHORT" : "LONG";
  const fill = toTradeNumber(overlay.fill_price) ?? entry;
  const mark =
    status === "closed"
      ? toTradeNumber(overlay.exit_price)
      : toTradeNumber(overlay.live_price);
  const metrics = calculateTradeMetrics({
    direction,
    fillPrice: fill,
    markPrice: mark,
    quantity: overlay.quantity,
    pointValue: overlay.point_value,
    currency: overlay.pnl_currency,
  });
  const percent = metrics?.percentChange ?? null;
  const currencyPnl = formatSignedTradeCurrency(
    metrics?.monetaryChange,
    metrics?.currency,
  );
  const pnl = currencyPnl
    ? ` ${currencyPnl}`
    : percent == null
      ? ""
      : ` ${percent >= 0 ? "+" : ""}${percent.toFixed(2)}%`;
  const quantity = formatTradeQuantity(overlay.quantity);

  if (status === "pending_entry") {
    return {
      status,
      badge: "WAITING FOR ENTRY",
      badgeColor: "#60a5fa",
      targetLabel: "TP QUEUED",
      entryLabel: direction === "short" ? "SELL LIMIT" : "BUY LIMIT",
      stopLabel: "SL QUEUED",
      regionAlpha: 0.08,
    };
  }
  if (status === "active") {
    return {
      status,
      badge: [`OPEN ${side}`, `${quantity} QTY`, pnl.trim()].filter(Boolean).join(" · "),
      badgeColor: percent != null && percent < 0 ? "#f87171" : "#2dd4a7",
      targetLabel: "TP ACTIVE",
      entryLabel: `FILLED ${side}`,
      stopLabel: "SL ACTIVE",
      regionAlpha: 0.16,
    };
  }
  if (status === "closed") {
    const targetHit = overlay.exit_reason === "price_target";
    return {
      status,
      badge: [`CLOSED ${side}`, `${quantity} QTY`, pnl.trim()].filter(Boolean).join(" · "),
      badgeColor: percent != null && percent < 0 ? "#f87171" : "#94a3b8",
      targetLabel: targetHit ? "TARGET HIT" : "TP CLOSED",
      entryLabel: `CLOSED ${side}`,
      stopLabel: targetHit ? "SL CLOSED" : "STOP HIT",
      regionAlpha: 0.055,
    };
  }
  return {
    status: null,
    badge: null,
    badgeColor: "#60a5fa",
    targetLabel: "TARGET",
    entryLabel: direction === "short" ? "SHORT" : "BUY",
    stopLabel: "STOP",
    regionAlpha: 0.13,
  };
};

export const drawEditableOverlayGeometry = ({
  context,
  geometry,
  selectedId = null,
  compactLabels = false,
}) => {
  if (!context) return;

  (Array.isArray(geometry) ? geometry : []).forEach((item) => {
    const selected = String(item.id) === String(selectedId);
    const color = item.color || "#94a3b8";

    if (item.kind === "trade_execution") {
      const direction = String(item.overlay?.direction || "long").toLowerCase();
      const long = direction !== "short";
      drawExecutionArrow({
        context,
        point: item.entryPoint,
        pointing: long ? "up" : "down",
        color: long ? "#2dd4a7" : "#f87171",
        label: long ? "LONG" : "SHORT",
        selected,
        plotWidth: item.plotWidth,
        plotHeight: item.plotHeight,
      });
      drawExecutionArrow({
        context,
        point: item.exitPoint,
        pointing: long ? "down" : "up",
        color: "#fbbf24",
        label: long ? "EXIT LONG" : "EXIT SHORT",
        selected,
        plotWidth: item.plotWidth,
        plotHeight: item.plotHeight,
      });
      return;
    }

    if (item.kind === "price_zone") {
      const zoneHeight = Math.max(item.y2 - item.y1, 0);
      if (zoneHeight <= 0) return;
      context.save();
      context.fillStyle = colorWithAlpha(color, selected ? 0.18 : 0.13);
      context.strokeStyle = colorWithAlpha(color, selected ? 0.95 : 0.5);
      context.lineWidth = selected ? 2 : 1;
      context.setLineDash(selected ? [] : [6, 5]);
      context.fillRect(item.x1, item.y1, item.x2 - item.x1, zoneHeight);
      context.strokeRect(
        item.x1 + 0.5,
        item.y1 + 0.5,
        Math.max(item.x2 - item.x1 - 1, 0),
        Math.max(zoneHeight - 1, 0),
      );
      if (item.overlay.label) {
        context.setLineDash([]);
        context.fillStyle = color;
        context.font =
          "600 11px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
        context.textAlign = "right";
        context.textBaseline = "top";
        context.fillText(
          item.overlay.label,
          item.x2 - (compactLabels ? 78 : 8),
          item.y1 + 5,
        );
      }
      if (selected) {
        const handleX = Math.max(item.x1 + 18, item.x2 - 28);
        drawHandle(context, handleX, item.y1, color);
        drawHandle(context, handleX, item.y2, color);
      }
      context.restore();
      return;
    }

    if (item.kind === "trade_setup") {
      const { overlay } = item;
      const direction = String(overlay.direction || "long").toLowerCase();
      const entry = toTradeNumber(overlay.entry_price ?? overlay.entryPrice);
      const stop = toTradeNumber(overlay.stop_loss ?? overlay.stopLoss);
      const target = toTradeNumber(overlay.price_target ?? overlay.priceTarget);
      if (entry == null || stop == null || target == null) return;
      const risk = Math.abs(entry - stop);
      const reward = Math.abs(target - entry);
      const rewardRisk = risk > 0 ? reward / risk : null;
      const lifecycle = tradeLifecyclePresentation(overlay, direction, entry);
      const profitColor = "#2dd4a7";
      const riskColor = "#f87171";
      const entryColor = "#60a5fa";
      const left = item.x1;
      const width = Math.max(item.x2 - item.x1, 0);
      const targetTop = Math.min(item.targetY, item.entryY);
      const targetHeight = Math.abs(item.targetY - item.entryY);
      const stopTop = Math.min(item.stopY, item.entryY);
      const stopHeight = Math.abs(item.stopY - item.entryY);
      const handleX = Math.max(left + 18, item.x2 - 30);
      context.save();
      const selectedAlpha = lifecycle.status === "closed"
        ? lifecycle.regionAlpha
        : Math.max(lifecycle.regionAlpha, 0.19);
      context.fillStyle = colorWithAlpha(
        profitColor,
        selected ? selectedAlpha : lifecycle.regionAlpha,
      );
      context.fillRect(left, targetTop, width, targetHeight);
      context.fillStyle = colorWithAlpha(
        riskColor,
        selected ? selectedAlpha : lifecycle.regionAlpha,
      );
      context.fillRect(left, stopTop, width, stopHeight);
      [
        [item.targetY, profitColor, lifecycle.targetLabel, target, "target"],
        [item.entryY, entryColor, lifecycle.entryLabel, entry, "entry"],
        [item.stopY, riskColor, lifecycle.stopLabel, stop, "stop"],
      ].forEach(([lineY, lineColor, label, price, lineKind]) => {
        context.strokeStyle = colorWithAlpha(lineColor, selected ? 1 : 0.82);
        const isEntry = lineKind === "entry";
        context.lineWidth = isEntry ? 2.25 : 1.5;
        context.setLineDash(isEntry ? [] : [6, 4]);
        context.beginPath();
        context.moveTo(left, Math.round(lineY) + 0.5);
        context.lineTo(item.x2, Math.round(lineY) + 0.5);
        context.stroke();
        context.setLineDash([]);
        context.fillStyle = lineColor;
        context.font = "700 10px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
        context.textAlign = "left";
        const placeBelowEntry =
          isEntry &&
          lifecycle.status &&
          Math.abs(item.targetY - item.entryY) < 24;
        context.textBaseline = placeBelowEntry ? "top" : "bottom";
        context.fillText(
          `${label}  ${Number(price).toLocaleString(undefined, { maximumFractionDigits: 4 })}`,
          left + 8,
          placeBelowEntry ? lineY + 4 : lineY - 4,
        );
      });
      if (lifecycle.badge) {
        const badgeWidth = Math.min(
          Math.max(context.measureText(lifecycle.badge).width + 24, 112),
          Math.max(width - 16, 112),
        );
        const badgeX = Math.max(left + 8, item.x2 - badgeWidth - 8);
        const badgeY = item.entryY + 7;
        context.fillStyle = colorWithAlpha("#0f172a", 0.9);
        context.fillRect(badgeX, badgeY, badgeWidth, 24);
        context.fillStyle = lifecycle.badgeColor;
        context.fillRect(badgeX, badgeY, 3, 24);
        context.fillStyle = "#f8fafc";
        context.font = "750 10px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
        context.textAlign = "left";
        context.textBaseline = "middle";
        context.fillText(lifecycle.badge, badgeX + 12, badgeY + 12);
      }
      if (rewardRisk != null && !lifecycle.status) {
        const badgeY = (item.targetY + item.entryY) / 2 - 10;
        const badgeX = left + Math.max((width - 62) / 2, 8);
        context.fillStyle = colorWithAlpha("#0f172a", 0.78);
        context.fillRect(badgeX, badgeY, 62, 20);
        context.fillStyle = "#f8fafc";
        context.font = "700 10px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(`${rewardRisk.toFixed(2)}R`, badgeX + 31, badgeY + 10);
      }
      if (selected) {
        if (lifecycle.status !== "closed") {
          drawHandle(context, handleX, item.targetY, profitColor);
          if (lifecycle.status !== "active") {
            drawHandle(context, handleX, item.entryY, entryColor);
          }
          drawHandle(context, handleX, item.stopY, riskColor);
        }
      }
      context.restore();
      return;
    }

    if (item.kind === "trend_line") {
      context.save();
      context.strokeStyle = color;
      context.lineWidth = selected ? 3 : 2;
      context.lineCap = "round";
      context.setLineDash([]);
      context.beginPath();
      context.moveTo(item.x1, item.y1);
      context.lineTo(item.x2, item.y2);
      context.stroke();
      if (selected) {
        drawHandle(context, item.x1, item.y1, color);
        drawHandle(context, item.x2, item.y2, color);
      }
      context.restore();
      return;
    }

    if (item.kind === "horizontal_line" && selected) {
      context.save();
      context.strokeStyle = colorWithAlpha(color, 0.95);
      context.lineWidth = 3;
      context.setLineDash([]);
      context.beginPath();
      context.moveTo(item.x1, item.y1);
      context.lineTo(item.x2, item.y2);
      context.stroke();
      drawHandle(
        context,
        Math.max(item.x1 + 18, item.x2 - 28),
        item.y1,
        color,
      );
      context.restore();
    }
  });
};
